import React from "react";
import { FormSettingsSeparator } from "../../../Components/Common/Form/FormSeparator";
import { NumericInput } from "../../../Components/Map/Form/NumericInput";
import { Select as FormSelect } from "../../../Components/Map/Form/Select";
import { Loader } from "../../../Components/Loader";
import {
    domainType,
    gridSizes,
    windGridSizes,
    staticExpertGridSizes,
} from "../../../Components/Map/RasterizationSettings/RasterizationSettingsConst";
import { withTranslation } from "react-i18next";
import { postCustomTask } from "../../../api/caseApi";
import { getProjectByIdAsync } from "../../../api/projectsApi";
import { loadProjects } from "../../../redux/actions/projectsActions";
import { connect } from "react-redux";

const helpUrl =
    "https://palm_gui.pages.fraunhofer.de/palmgui_handbuch/domainmap.html#sec-domainrasterization";

class RuntimeEstimator extends React.Component {
    constructor(props) {
        super(props);
        const initialGridSizes = gridSizes[0];
        const initialGridWidth = 10;

        this.state = {
            applicationField: {
                label: this.props.t("ThermalComfort"),
                value: "ThermalComfort",
            },
            domainType: "SingleDomain",
            gridWidth: initialGridWidth,
            gridPointsX: 200,
            gridPointsY: 200,
            gridSizes: initialGridSizes,
            parentGridPointsX: 200,
            parentGridPointsY: 200,
            childGridPointsX: 200,
            childGridPointsY: 200,
            parentOffsetX: 0,
            parentOffsetY: 0,
            verticalResolution: initialGridWidth, // Default equals gridWidth for SingleDomain
            mode: {
                label: this.props.t("CyclicBoundaryConditions"),
                value: "CyclicBoundaryConditions",
            },
            duration: 30,
            windSpeed: 1.5,
            gridPointsZ: 160,
            isCalculating: false,
            estimateResult: null,
            windDirections: 8,
            maxHeight: 50,
            meanWindSpeed: 1.5,
            estimateRequestId: null,
            estimateError: null,
        };
        this.handleApplicationFieldChange = this.handleApplicationFieldChange.bind(
            this
        );
        this.handleDomainTypeChange = this.handleDomainTypeChange.bind(this);
        this.handleGridWidthChange = this.handleGridWidthChange.bind(this);
        this.handleGridPointsXChange = this.handleGridPointsXChange.bind(this);
        this.handleGridPointsYChange = this.handleGridPointsYChange.bind(this);
        this.handleGridSizesChange = this.handleGridSizesChange.bind(this);
        this.handleParentGridPointsXChange =
            this.handleParentGridPointsXChange.bind(this);
        this.handleParentGridPointsYChange =
            this.handleParentGridPointsYChange.bind(this);
        this.handleChildGridPointsXChange =
            this.handleChildGridPointsXChange.bind(this);
        this.handleChildGridPointsYChange =
            this.handleChildGridPointsYChange.bind(this);
        this.handleParentOffsetXChange = this.handleParentOffsetXChange.bind(this);
        this.handleParentOffsetYChange = this.handleParentOffsetYChange.bind(this);
        this.handleVerticalResolutionChange = this.handleVerticalResolutionChange.bind(this);
        this.handleModeChange = this.handleModeChange.bind(this);
        this.handleDurationChange = this.handleDurationChange.bind(this);
        this.handleWindSpeedChange = this.handleWindSpeedChange.bind(this);
        this.handleGridPointsZChange = this.handleGridPointsZChange.bind(this);
        this.handleWindDirectionsChange = this.handleWindDirectionsChange.bind(this);
        this.handleMaxHeightChange = this.handleMaxHeightChange.bind(this);
        this.handleMeanWindSpeedChange = this.handleMeanWindSpeedChange.bind(this);
        this.createEstimate = this.createEstimate.bind(this);
    }

    componentDidMount() {
        if (!this.props.projects || this.props.projects?.length === 0) {
            this.props.loadProjects?.();
        }
    }

    componentDidUpdate(prevProps) {
        const prevNote = prevProps.osmNotification;
        const note = this.props.osmNotification;

        if (!note || note === prevNote) {
            return;
        }

        // Only react to notifications belonging to our currently pending estimate request
        if (!this.state.estimateRequestId || note.requestId !== this.state.estimateRequestId) {
            return;
        }

        // status: 2 = finished, 3 = error (matches existing OSM Download usage)
        if (note.status === 2) {
            const estimate =
                note?.response?.estimate ??
                note?.response?.estimation ??
                note?.response?.value ??
                note?.response?.resultUrl ??
                null;

            this.setState({
                estimateResult: estimate,
                isCalculating: false,
                estimateRequestId: null,
                estimateError: null,
            });
        } else if (note.status === 3) {
            this.setState({
                isCalculating: false,
                estimateRequestId: null,
                estimateError: note?.error || "Estimate request failed",
            });
        }
    }

    async resolveCaseId() {
        // Best UX: use currently selected/active case (if any)
        if (this.props.currentCaseId) {
            return this.props.currentCaseId;
        }

        // Fallback: pick the first case of the first project that has one
        if (!this.props.projects || this.props.projects.length === 0) {
            return null;
        }

        for (let index = 0; index < this.props.projects.length; index++) {
            const projectId = this.props.projects[index].id;
            const projectDetails = await getProjectByIdAsync(projectId);
            if (projectDetails.caseDetailsDto && projectDetails.caseDetailsDto.length > 0) {
                return projectDetails.caseDetailsDto[0].id;
            }
        }

        return null;
    }

    parseParentGridWidth(gridSizesValue) {
        // Parse "Parent 10m / Child 2m" -> 10
        const match = gridSizesValue.match(/Parent\s+(\d+(?:\.\d+)?)m/);
        return match ? parseFloat(match[1]) : 10;
    }

    getApplicationFieldNumber() {
        // ThermalComfort = 1, WindComfort = 2
        return this.state.applicationField.value === "WindComfort" ? 2 : 1;
    }

    getInitialState() {
        const initialGridSizes = gridSizes[0];
        const initialGridWidth = 10;
        
        return {
            domainType: "SingleDomain",
            gridWidth: initialGridWidth,
            gridPointsX: 200,
            gridPointsY: 200,
            gridSizes: initialGridSizes,
            parentGridPointsX: 200,
            parentGridPointsY: 200,
            childGridPointsX: 200,
            childGridPointsY: 200,
            parentOffsetX: 0,
            parentOffsetY: 0,
            verticalResolution: initialGridWidth, // Default equals gridWidth for SingleDomain
            mode: {
                label: this.props.t("CyclicBoundaryConditions"),
                value: "CyclicBoundaryConditions",
            },
            duration: 30,
            windSpeed: 1.5,
            gridPointsZ: 160,
            isCalculating: false,
            estimateResult: null,
            windDirections: 8,
            maxHeight: 50,
            meanWindSpeed: 1.5,
        };
    }

    handleApplicationFieldChange(e) {
        const newApplicationField = e.target.value;
        const initialState = this.getInitialState();
        
        // Update vertical resolution based on new domain type
        let newVerticalResolution;
        if (initialState.domainType === "SingleDomain") {
            newVerticalResolution = initialState.gridWidth;
        } else {
            newVerticalResolution = this.parseParentGridWidth(initialState.gridSizes);
        }

        this.setState({
            applicationField: {
                label: this.props.t(newApplicationField),
                value: newApplicationField,
            },
            ...initialState,
            verticalResolution: newVerticalResolution,
        });
    }

    handleDomainTypeChange(e) {
        const newDomainType = e.target.value;
        // Update vertical resolution based on domain type
        let newVerticalResolution;
        if (newDomainType === "SingleDomain") {
            newVerticalResolution = this.state.gridWidth;
        } else {
            // NestedDomain - parse from gridSizes
            newVerticalResolution = this.parseParentGridWidth(this.state.gridSizes);
        }

        this.setState({
            domainType: newDomainType,
            verticalResolution: newVerticalResolution,
        });
    }

    handleGridWidthChange(e) {
        const newGridWidth = e.target.value;
        this.setState({
            gridWidth: newGridWidth,
            // Update vertical resolution when grid width changes (for SingleDomain)
            verticalResolution: this.state.domainType === "SingleDomain"
                ? Number(newGridWidth)
                : this.state.verticalResolution,
        });
    }

    handleGridPointsXChange(e) {
        this.setState({
            gridPointsX: e.target.value,
        });
    }

    handleGridPointsYChange(e) {
        this.setState({
            gridPointsY: e.target.value,
        });
    }

    handleGridSizesChange(e) {
        const newGridSizes = e.target.value;
        const parentGridWidth = this.parseParentGridWidth(newGridSizes);
        this.setState({
            gridSizes: newGridSizes,
            // Update vertical resolution when grid sizes change (for NestedDomain)
            verticalResolution: this.state.domainType === "NestedDomain"
                ? parentGridWidth
                : this.state.verticalResolution,
        });
    }

    handleParentGridPointsXChange(e) {
        this.setState({
            parentGridPointsX: e.target.value,
        });
    }

    handleParentGridPointsYChange(e) {
        this.setState({
            parentGridPointsY: e.target.value,
        });
    }

    handleChildGridPointsXChange(e) {
        this.setState({
            childGridPointsX: e.target.value,
        });
    }

    handleChildGridPointsYChange(e) {
        this.setState({
            childGridPointsY: e.target.value,
        });
    }

    handleParentOffsetXChange(e) {
        this.setState({
            parentOffsetX: e.target.value,
        });
    }

    handleParentOffsetYChange(e) {
        this.setState({
            parentOffsetY: e.target.value,
        });
    }

    handleVerticalResolutionChange(e) {
        this.setState({
            verticalResolution: e.target.value,
        });
    }

    handleModeChange(e) {
        this.setState({
            mode: {
                label: e.target.value,
                value: e.target.value,
            },
        });
    }

    handleDurationChange(e) {
        this.setState({
            duration: e.target.value,
        });
    }

    handleWindSpeedChange(e) {
        this.setState({
            windSpeed: e.target.value,
        });
    }

    handleGridPointsZChange(e) {
        this.setState({
            gridPointsZ: e.target.value,
        });
    }

    handleWindDirectionsChange(e) {
        this.setState({
            windDirections: Number(e.target.value),
        });
    }

    handleMaxHeightChange(e) {
        this.setState({
            maxHeight: e.target.value,
        });
    }

    handleMeanWindSpeedChange(e) {
        this.setState({
            meanWindSpeed: e.target.value,
        });
    }

    async createEstimate() {
        this.setState({ isCalculating: true, estimateError: null });
        try {
            // Prepare the request payload with all the form data
            const payload = {
                applicationField: this.state.applicationField.value,
                domainType: this.state.domainType,
                gridWidth: Number(this.state.gridWidth),
                gridPointsX: Number(this.state.gridPointsX),
                gridPointsY: Number(this.state.gridPointsY),
                gridSizes: this.state.gridSizes,
                parentGridPointsX: Number(this.state.parentGridPointsX),
                parentGridPointsY: Number(this.state.parentGridPointsY),
                childGridPointsX: Number(this.state.childGridPointsX),
                childGridPointsY: Number(this.state.childGridPointsY),
                parentOffsetX: Number(this.state.parentOffsetX),
                parentOffsetY: Number(this.state.parentOffsetY),
                verticalResolution: Number(this.state.verticalResolution),
                mode: this.state.mode.value,
                duration: Number(this.state.duration),
                windSpeed: Number(this.state.windSpeed),
                gridPointsZ: Number(this.state.gridPointsZ),
            };

            // Note: we currently use the generic task API (POST /api/task) instead of
            // a dedicated synchronous endpoint.

            const caseId = await this.resolveCaseId();
            if (!caseId) {
                // No case exists to attach the task to (API requires a caseId)
                this.setState({ isCalculating: false, estimateResult: null, estimateRequestId: null });
                return;
            }

            const request = {
                caseId: caseId,
                type: "custom-tasks",
                request: {
                  subtask: "estimateRuntime",
                  contract: payload
                },
            };
            console.log(JSON.stringify(request, null, 2));
            this.setState({
                estimateRequestId: await postCustomTask(request),
            });
        } catch (error) {
            console.error("Error creating estimate:", error);
            this.setState({
                isCalculating: false,
                estimateResult: null,
                estimateRequestId: null,
                estimateError: "Estimate request failed",
            });
        }
    }

    render() {
        const { t } = this.props;
        const applicationFieldOptions = [
            "ThermalComfort",
            "WindComfort",
        ];

        const modeOptions = [
            "CyclicBoundaryConditions",
            "DynamicDriver",
        ];

        const appField = this.getApplicationFieldNumber();
        const isWindComfort = appField === 2;

        // Grid Points min/max based on applicationField
        const gridPointsMin = isWindComfort ? 30 : 100;
        const gridPointsMax = isWindComfort ? 600 : 1000;
        const parentGridPointsMax =
            isWindComfort
                ? 600
                : appField === 1
                    ? 1000
                    : appField === 0 || appField === 4
                        ? 1000
                        : 400;
        const childGridPointsMax =
            isWindComfort
                ? 600
                : appField === 1
                    ? 600
                    : appField === 0 || appField === 4
                        ? 1000
                        : 400;

        // Grid Sizes options based on applicationField
        const gridSizesOptions =
            isWindComfort
                ? windGridSizes
                : appField === 0 || appField === 4
                    ? staticExpertGridSizes
                    : gridSizes;

        return (
            <div className="wind-wrapper" style={{ width: "500px" }}>
                <div
                    style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        marginBottom: 12,
                    }}
                >
                    <h2 style={{ fontSize: 28, margin: 0 }}>{t("RuntimeEstimator")}</h2>
                </div>

                <FormSettingsSeparator label={t("ApplicationField")} />


                <FormSelect
                    label={t("ApplicationField")}
                    value={this.state.applicationField.value}
                    options={applicationFieldOptions.map((x) => {
                        return { label: t(x), value: x };
                    })}
                    onChange={this.handleApplicationFieldChange}
                    help={helpUrl}
                />

                <div style={{ marginBottom: 18 }} />

                <FormSettingsSeparator label={t("Domain")} />

                <FormSelect
                    label={t("SelectDomainType")}
                    value={this.state.domainType}
                    options={domainType.map((x) => {
                        return { label: t(`${x}`), value: x };
                    })}
                    onChange={this.handleDomainTypeChange}
                    help={helpUrl}
                />

                {this.state.domainType === "SingleDomain" && (
                    <>
                        <NumericInput
                            name={"Grid Width"}
                            label={t("GridWidth")}
                            value={isWindComfort ? 1 : this.state.gridWidth}
                            min={"1"}
                            max={"50"}
                            step={"1"}
                            unit={"m"}
                            maxLength={"2"}
                            onChange={this.handleGridWidthChange}
                            disabled={isWindComfort}
                            onBlur={(e) => {
                                if (
                                    this.state.gridWidth < Number(e.target.min) ||
                                    this.state.gridWidth > Number(e.target.max)
                                ) {
                                    this.setState({ gridWidth: 5 });
                                }
                            }}
                            help={helpUrl}
                        />
                        <NumericInput
                            name={"No.ofGridPointsInX-Direction"}
                            label={t("NoOfGridPointsInXDirection")}
                            value={this.state.gridPointsX}
                            maxLength={"3"}
                            min={gridPointsMin}
                            max={gridPointsMax}
                            step={2}
                            unit={"-"}
                            onChange={this.handleGridPointsXChange}
                            onBlur={(e) => {
                                if (
                                    this.state.gridPointsX < Number(e.target.min) ||
                                    this.state.gridPointsX > Number(e.target.max)
                                ) {
                                    this.setState({ gridPointsX: 200 });
                                }
                            }}
                            help={helpUrl}
                        />
                        <NumericInput
                            name={"No.ofGridPointsInY-Direction"}
                            label={t("NoOfGridPointsInYDirection")}
                            value={this.state.gridPointsY}
                            maxLength={"3"}
                            min={gridPointsMin}
                            max={gridPointsMax}
                            step={"2"}
                            unit={"-"}
                            onChange={this.handleGridPointsYChange}
                            onBlur={(e) => {
                                if (
                                    this.state.gridPointsY < Number(e.target.min) ||
                                    this.state.gridPointsY > Number(e.target.max)
                                ) {
                                    this.setState({ gridPointsY: 200 });
                                }
                            }}
                            help={helpUrl}
                        />
                    </>
                )}

                {this.state.domainType === "NestedDomain" && (
                    <>
                        <FormSelect
                            label={t("SelectGridSizes")}
                            value={this.state.gridSizes}
                            options={gridSizesOptions.map((x) => {
                                return { label: t(`${x}`), value: x };
                            })}
                            onChange={this.handleGridSizesChange}
                            help={helpUrl}
                        />
                        <NumericInput
                            name={"Parent: No. of GridPoints in x-Direction"}
                            label={t("ParentNumberOfGridPointsInXDirection")}
                            value={this.state.parentGridPointsX}
                            min={isWindComfort ? "30" : "100"}
                            max={String(parentGridPointsMax)}
                            step={"2"}
                            maxLength={"3"}
                            unit={"-"}
                            onChange={this.handleParentGridPointsXChange}
                            onBlur={(e) => {
                                if (
                                    this.state.parentGridPointsX < Number(e.target.min) ||
                                    this.state.parentGridPointsX > Number(e.target.max)
                                ) {
                                    this.setState({ parentGridPointsX: 200 });
                                }
                            }}
                            help={helpUrl}
                        />
                        <NumericInput
                            name={"Parent: No.ofGridPoints in y-Direction"}
                            label={t("ParentNumberOfGridPointsInYDirection")}
                            value={this.state.parentGridPointsY}
                            min={isWindComfort ? "30" : "100"}
                            max={String(parentGridPointsMax)}
                            step={"2"}
                            maxLength={"3"}
                            onChange={this.handleParentGridPointsYChange}
                            unit={"-"}
                            onBlur={(e) => {
                                if (
                                    this.state.parentGridPointsY < Number(e.target.min) ||
                                    this.state.parentGridPointsY > Number(e.target.max)
                                ) {
                                    this.setState({ parentGridPointsY: 200 });
                                }
                            }}
                            help={helpUrl}
                        />
                        <NumericInput
                            name={"Child: No. of GridPoints in x-Direction"}
                            label={t("ChildNumberOfGridPointsInXDirection")}
                            value={this.state.childGridPointsX}
                            min={isWindComfort ? "30" : "100"}
                            max={String(childGridPointsMax)}
                            step={"2"}
                            maxLength={"3"}
                            unit={"-"}
                            onChange={this.handleChildGridPointsXChange}
                            onBlur={(e) => {
                                if (
                                    this.state.childGridPointsX < Number(e.target.min) ||
                                    this.state.childGridPointsX > Number(e.target.max)
                                ) {
                                    this.setState({ childGridPointsX: 200 });
                                }
                            }}
                            help={helpUrl}
                        />
                        <NumericInput
                            name={"Child: No.ofGridPoints in y-Direction"}
                            label={t("ChildNumberOfGridPointsInYDirection")}
                            value={this.state.childGridPointsY}
                            min={isWindComfort ? "30" : "100"}
                            max={String(childGridPointsMax)}
                            step={"2"}
                            maxLength={"3"}
                            unit={"-"}
                            onChange={this.handleChildGridPointsYChange}
                            onBlur={(e) => {
                                if (
                                    this.state.childGridPointsY < Number(e.target.min) ||
                                    this.state.childGridPointsY > Number(e.target.max)
                                ) {
                                    this.setState({ childGridPointsY: 200 });
                                }
                            }}
                            help={helpUrl}
                        />
                        <NumericInput
                            name={"ParentOffsetXDirection"}
                            label={t("ParentOffsetXDirection")}
                            value={this.state.parentOffsetX}
                            min={"-4999"}
                            max={"4999"}
                            step={"1"}
                            maxLength={"4"}
                            onChange={this.handleParentOffsetXChange}
                            unit={"-"}
                            onBlur={(e) => {
                                if (
                                    this.state.parentOffsetX > Number(e.target.max) ||
                                    this.state.parentOffsetX < Number(e.target.min) ||
                                    this.state.parentOffsetX === ""
                                ) {
                                    this.setState({ parentOffsetX: 0 });
                                }
                            }}
                            help={helpUrl}
                        />
                        <NumericInput
                            name={"ParentOffsetYDirection"}
                            label={t("ParentOffsetYDirection")}
                            value={this.state.parentOffsetY}
                            min={"-4999"}
                            max={"4999"}
                            step={1}
                            maxLength={4}
                            onChange={this.handleParentOffsetYChange}
                            unit={"-"}
                            onBlur={(e) => {
                                if (
                                    this.state.parentOffsetY < -4999 ||
                                    this.state.parentOffsetY > 4999 ||
                                    this.state.parentOffsetY === ""
                                ) {
                                    this.setState({ parentOffsetY: 0 });
                                }
                            }}
                            help={helpUrl}
                        />
                    </>
                )}

                {appField === 1 && (
                    <NumericInput
                        name={"VerticalResolution"}
                        label={t("VerticalResolution")}
                        value={this.state.verticalResolution}
                        min={"1"}
                        max={"50"}
                        step={"1"}
                        unit={"m"}
                        maxLength={"2"}
                        onChange={this.handleVerticalResolutionChange}
                        onBlur={(e) => {
                            if (
                                this.state.verticalResolution < Number(e.target.min) ||
                                this.state.verticalResolution > Number(e.target.max)
                            ) {
                                // Reset to appropriate default based on domain type
                                const defaultValue = this.state.domainType === "SingleDomain"
                                    ? this.state.gridWidth
                                    : this.parseParentGridWidth(this.state.gridSizes);
                                this.setState({ verticalResolution: defaultValue });
                            }
                        }}
                        help={helpUrl}
                    />
                )}

                <div style={{ marginBottom: 18 }} />

                <FormSettingsSeparator label={t("Setup")} />

                {appField === 1 && (
                    <>
                        <FormSelect
                            label={t("Mode")}
                            value={this.state.mode.value}
                            options={modeOptions.map((x) => {
                                return { label: t(x), value: x };
                            })}
                            onChange={this.handleModeChange}
                            help={helpUrl}
                        />

                        <NumericInput
                            name={"Duration"}
                            label={t("Duration")}
                            value={this.state.duration}
                            min={"1"}
                            max={"999"}
                            step={"1"}
                            unit={"h"}
                            maxLength={"3"}
                            onChange={this.handleDurationChange}
                            onBlur={(e) => {
                                if (
                                    this.state.duration < Number(e.target.min) ||
                                    this.state.duration > Number(e.target.max)
                                ) {
                                    this.setState({ duration: 30 });
                                }
                            }}
                            help={helpUrl}
                        />

                        <NumericInput
                            name={"WindSpeed"}
                            label={t("WindSpeed")}
                            value={this.state.windSpeed}
                            min={"0"}
                            max={"100"}
                            step={"0.1"}
                            unit={"m/s"}
                            maxLength={"5"}
                            onChange={this.handleWindSpeedChange}
                            onBlur={(e) => {
                                if (
                                    this.state.windSpeed < Number(e.target.min) ||
                                    this.state.windSpeed > Number(e.target.max)
                                ) {
                                    this.setState({ windSpeed: 1.5 });
                                }
                            }}
                            help={helpUrl}
                        />

                        {this.state.mode.value === "DynamicDriver" && (
                            <NumericInput
                                name={"Grid Points z-Direction"}
                                label={t("GridPointsZDirection")}
                                value={this.state.gridPointsZ}
                                maxLength={"3"}
                                min={"1"}
                                max={"1000"}
                                step={"1"}
                                unit={"-"}
                                onChange={this.handleGridPointsZChange}
                                onBlur={(e) => {
                                    if (
                                        this.state.gridPointsZ < Number(e.target.min) ||
                                        this.state.gridPointsZ > Number(e.target.max)
                                    ) {
                                        this.setState({ gridPointsZ: 160 });
                                    }
                                }}
                                help={helpUrl}
                            />
                        )}
                    </>
                )}

                {appField === 2 && (
                    <>
                        <FormSelect
                            label={t("WindDirections")}
                            value={String(this.state.windDirections)}
                            options={[
                                { label: t("8WindDirections"), value: "8" },
                                { label: t("12WindDirections"), value: "12" },
                            ]}
                            onChange={this.handleWindDirectionsChange}
                            help={helpUrl}
                        />

                        <NumericInput
                            name={"Max Height Building Topography"}
                            label={t("MaxHeightBuildingTopography")}
                            value={this.state.maxHeight}
                            min={"1"}
                            max={"1000"}
                            step={"1"}
                            unit={"m"}
                            maxLength={"4"}
                            onChange={this.handleMaxHeightChange}
                            onBlur={(e) => {
                                if (
                                    this.state.maxHeight < Number(e.target.min) ||
                                    this.state.maxHeight > Number(e.target.max)
                                ) {
                                    this.setState({ maxHeight: 50 });
                                }
                            }}
                            help={helpUrl}
                        />

                        <NumericInput
                            name={"MeanWindSpeed"}
                            label={t("MeanWindSpeed")}
                            value={this.state.meanWindSpeed}
                            min={"0"}
                            max={"100"}
                            step={"0.1"}
                            unit={"m/s"}
                            maxLength={"5"}
                            onChange={this.handleMeanWindSpeedChange}
                            onBlur={(e) => {
                                if (
                                    this.state.meanWindSpeed < Number(e.target.min) ||
                                    this.state.meanWindSpeed > Number(e.target.max)
                                ) {
                                    this.setState({ meanWindSpeed: 1.5 });
                                }
                            }}
                            help={helpUrl}
                        />
                    </>
                )}

                <div style={{ marginBottom: 18 }} />

                <FormSettingsSeparator label={t("Estimate")} />

                <div
                    style={{
                        display: "flex",
                        flexDirection: "row",
                        alignItems: "center",
                        justifyContent: "space-between"
                    }}
                >
                    <button
                        className="wizard-button wizard-button-submit"
                        onClick={this.createEstimate}
                        disabled={this.state.isCalculating}
                        style={{
                            display: "flex",
                            justifyContent: "center",
                            height: "34px",
                            width: "auto",
                        }}
                    >
                        {this.state.isCalculating && (
                            <Loader size="x-small" mode="light" />
                        )}
                        {!this.state.isCalculating && (
                            <span>{t("CreateEstimate")}</span>
                        )}
                    </button>

                    <input
                        className="setup-input"
                        type="text"
                        readOnly
                        value={
                            this.state.estimateResult !== null
                                ? `${this.state.estimateResult} [h]`
                                : ""
                        }
                        placeholder=""
                        style={{
                            flex: 1,
                            backgroundColor: "#f5f5f5",
                            border: "1px solid #d0d0d0",
                            borderRadius: "3px",
                            padding: "6px 12px",
                            fontSize: "14px",
                            color: "#494949",
                            cursor: "default",
                            maxWidth: "284px",
                            marginBottom: "0px"
                        }}
                    />
                </div>

                {this.state.estimateError && (
                    <div className="sim-warning" style={{ marginTop: "8px" }}>
                        {this.state.estimateError}
                    </div>
                )}

                {appField === 2 && (
                    <div
                        style={{
                            fontSize: "12px",
                            color: "#494949",
                            marginTop: "8px",
                            textAlign: "right",
                            fontStyle: "italic"
                        }}
                    >
                        {t("EstimatedTotalRuntimeForAll")} {this.state.windDirections} {t("WindDirections")}
                    </div>
                )}

                <div style={{ marginBottom: "12px" }} />
                <div
                    style={{
                        fontSize: "11px",
                        color: "#999999",
                        fontStyle: "italic",
                        marginTop: "12px",
                        lineHeight: "1.5",
                    }}
                >
                    {t("RuntimeEstimateNote1")}
                </div>

                <div
                    style={{
                        fontSize: "11px",
                        color: "#999999",
                        fontStyle: "italic",
                        marginTop: "8px",
                        lineHeight: "1.5",
                    }}
                >
                    {t("RuntimeEstimateNote2")}
                </div>
            </div>
        );
    }
}

const mapStateToProps = (state) => {
    return {
        currentCaseId: state.projects?.currentCaseId,
        projects: state.projects?.projects,
        osmNotification: state.tools?.osmNotification,
    };
};

const mapDispatchToProps = (dispatch) => {
    return {
        loadProjects: () => dispatch(loadProjects()),
    };
};

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(withTranslation()(RuntimeEstimator));
